import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { Add, Ai, Delete, Pencil } from '@factorialco/factorial-one-react/icons/app'
import {
  EmployeeCollectionFilterEmployeesNamesDocument,
  EmployeeCollectionFilterLocationNamesDocument,
  EmployeeCollectionFilterRolesNamesDocument,
  EmployeeCollectionFilterTeamNamesDocument,
  EmployeeCollectionPermissionsDocument,
  EmployeesCollectionListDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const CollectionsPage = () => {
  const client = useApolloClient()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()
  const navigate = useNavigate()

  /**
   * Get Permissions
   */
  const [permissions, setPermissions] = useState({
    canCreateEmployee: false,
    canManagePayroll: false,
  })

  useEffect(() => {
    const getPermissions = async () => {
      const { data } = await client.query({
        query: EmployeeCollectionPermissionsDocument,
      })

      setPermissions({
        canCreateEmployee: data.apiCore._policies.employeeCreate.can,
        canManagePayroll: data.payroll._policies.companyManagePayroll.can,
      })
    }
    getPermissions()
  }, [])

  const primaryActions = () => {
    if (permissions.canCreateEmployee) {
      return {
        label: i18n.t('employees.new_employee'),
        icon: Add,
        onClick: () => {
          navigate(navigation.employees.list.add_employee.fullPath())
        },
      }
    }

    return undefined
  }

  const source = useGraphqlDataSource(
    {
      document: EmployeesCollectionListDocument,
      mapVariables: ({ filters, sortings, search }) => {
        const teamIds = filters.team?.map((id) => id)
        const locationIds = filters.workplace?.map((id) => id)
        const jobRoleIds = filters.jobRole?.map((id) => id)
        return {
          companyId: company.id,
          onlyManagers: filters.jobRole?.includes(2),
          // onlyDirectReports: filters.jobRole?.includes('report'),
          onlyMyReportees: filters.directReports?.includes(true),
          reportingTo: filters.reportingTo?.map((id) => id),
          jobRole: jobRoleIds?.length ? jobRoleIds : undefined,
          onlyActive: filters.active?.includes(true),
          sort: sortings?.[0],
          search: search,
          teamIds: teamIds?.length ? teamIds : undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
        }
      },
      grouping: {
        collapsible: true,
        groupBy: {
          city: {
            name: 'Location',
            label: (groupId) => String(groupId),
          },
        },
      },
      mapPagination: (data) => {
        return {
          total: data.employees.employeesConnection.totalCount ?? 0,
        }
      },
      mapData: (data) => data.employees.employeesConnection.nodes,
      itemUrl: (item) => navigation.employees.id.fullPath({ id: item.id }),
      sortings: {
        firstName: {
          label: i18n.t('employee_form.first_name'),
        },
        lastName: {
          label: i18n.t('employee_form.last_name'),
        },
        'referenceContract.jobTitle': {
          label: i18n.t('common.job'),
        },
        startsOn: {
          label: i18n.t('employee_form.hire_date'),
        },
        city: {
          label: i18n.t('common.location'),
        },
        active: {
          label: i18n.t('employee_form.status'),
        },
        team: {
          label: i18n.t('common.team'),
        },
        workplace: {
          label: i18n.t('common.location'),
        },
        directReports: {
          label: i18n.t('employees.orgchart.direct_reports'),
        },
        jobRole: {
          label: i18n.t('common.job_title'),
        },
      },
      defaultSorting: {
        field: 'firstName',
        order: 'asc',
      },
      search: {
        enabled: true,
      },
      filters: {
        team: {
          type: 'in',
          label: 'Team',
          options: {
            options: async () => {
              const { data } = await client.query({
                query: EmployeeCollectionFilterTeamNamesDocument,
                fetchPolicy: 'cache-first',
              })

              return data.teams.teams.map((team) => ({
                label: team.name,
                value: team.id,
              }))
            },
          },
        },
        workplace: {
          type: 'in',
          label: 'Workplace',
          options: {
            options: async () => {
              const { data } = await client.query({
                query: EmployeeCollectionFilterLocationNamesDocument,
                fetchPolicy: 'cache-first',
              })

              return data.locations.locationsConnection.nodes.map((location) => ({
                label: location.name,
                value: location.id,
              }))
            },
          },
        },
        active: {
          type: 'in',
          label: 'Status',
          options: { options: [{ label: 'Only Active', value: true }] },
        },
        jobRole: {
          type: 'in',
          label: 'Role',
          options: {
            options: async () => {
              const { data } = await client.query({
                query: EmployeeCollectionFilterRolesNamesDocument,
                fetchPolicy: 'cache-first',
              })

              return data.jobCatalog.rolesConnection.nodes.map((role) => ({
                label: role.name,
                value: role.id,
              }))
            },
          },
        },
        directReports: {
          type: 'in',
          label: 'Managed by me',
          options: {
            options: [
              { label: 'Managed by me', value: true },
              { label: 'Managed by others', value: false },
            ],
          },
        },
        reportingTo: {
          type: 'in',
          label: 'Reporting to',
          options: {
            options: async () => {
              const { data } = await client.query({
                query: EmployeeCollectionFilterEmployeesNamesDocument,
                variables: {
                  companyId: company.id,
                },
                fetchPolicy: 'cache-first',
              })

              return data.employees.employeeNames.map((employee) => ({
                label: employee.fullName,
                value: employee.id,
                avatar: {
                  type: 'person',
                  firstName: employee.preferredName || employee.firstName,
                  lastName: employee.lastName,
                  src: employee.avatar?.url || undefined,
                },
              }))
            },
          },
        },
      },
      presets: [
        {
          label: 'Managed by me',
          filter: {
            directReports: [true],
          },
        },
      ],
      primaryActions,
      secondaryActions: () => [
        {
          label: 'Import people',
          onClick: () => {
            alert('Add employee')
          },
        },
        {
          label: 'Edit people',
          onClick: () => {
            alert('Add employee')
          },
        },
        {
          label: 'Add time off',
          onClick: () => {
            alert('Delete employee')
          },
        },
        {
          label: 'Import time off allowances',
          onClick: () => {
            alert('Delete employee')
          },
        },
        {
          label: 'Export data',
          onClick: () => {
            alert('Export data')
          },
        },
        {
          label: 'Import data to tables',
          onClick: () => {
            alert('Import data to tables')
          },
        },
      ],
      selectable: (item) => item.id,
      bulkActions: () => ({
        primary: [
          {
            id: 'edit-employees',
            label: 'Edit Employees',
            icon: Pencil,
            onClick: () => {
              alert('Edit employee')
            },
          },
          {
            id: 'edit-suplements',
            label: 'Edit suplements',
            icon: Pencil,
            onClick: () => {
              alert('Edit suplements')
            },
          },
        ],
      }),
      itemActions: (item) => [
        {
          label: 'Edit Employee',
          icon: Pencil,
          onClick: () => {
            alert(`Edit employee ${item.fullName}`)
          },
        },
        {
          label: 'Promote with AI',
          icon: Ai,
          onClick: () => {
            alert(`${item.fullName} will be promoted according to the magic of AI`)
          },
        },
        { type: 'separator' },
        {
          label: 'Terminate employee',
          icon: Delete,
          critical: true,
          description: 'This action cannot be undone',
          onClick: () => {
            alert(`Terminate employee ${item.fullName}`)
          },
        },
      ],
    },
    [company.id]
  )

  const handleBulkAction = (
    action: string,
    {
      allSelected,
      itemsStatus,
    }: {
      allSelected: boolean | 'indeterminate'
      itemsStatus: readonly { item: { id: number }; checked: boolean }[]
    }
  ) => {
    if (allSelected === true) {
      alert('Please select at least one employee')
      return
    }

    const selectedEmployeesIds = itemsStatus
      .filter((item) => item.checked)
      .map((item) => item.item.id)
    switch (action) {
      case 'edit-employees':
        navigate(navigation.employees.edit.fullPath(), {
          state: {
            employeesIds: selectedEmployeesIds,
          },
        })
        break
      case 'edit-suplements':
        navigate(navigation.employees.bulkFixContracts.fullPath(), {
          state: {
            employeesIds: selectedEmployeesIds,
          },
        })
        break
    }
  }

  return (
    <ResponsivePage.Body height="none">
      <OneDataCollection
        fullHeight
        source={source}
        onBulkAction={handleBulkAction}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('employee_form.first_name'),
                  sorting: 'firstName',
                  render: (item) => ({
                    type: 'person',
                    value: {
                      firstName: item.firstName,
                      lastName: '',
                      avatar: item.access.avatar,
                    },
                  }),
                },
                {
                  label: 'Last Name',
                  sorting: 'lastName',
                  render: (item) => item.lastName,
                },
                {
                  label: 'Job',
                  sorting: 'referenceContract.jobTitle',
                  render: (item) => item.referenceContract?.jobTitle ?? '',
                },
                {
                  label: 'Location',
                  sorting: 'city',
                  render: (item) => item.city ?? '',
                },
                {
                  label: 'Hire Date',
                  sorting: 'startsOn',
                  render: (item) => item.referenceContract?.startsOn ?? '',
                },
                {
                  label: 'Status',
                  sorting: 'active',
                  render: (item) => ({
                    type: 'status',
                    value: {
                      label: item.active ? 'Active' : 'Inactive',
                      // i18n.t('employees.status.active') : i18n.t('employees.status.inactive'),
                      status: item.active ? 'positive' : 'neutral',
                    },
                  }),
                },
              ],
            },
          },
          {
            type: 'card',
            options: {
              title: (item) => item.fullName,
              cardProperties: [
                {
                  label: 'City',
                  render: (item) => ({
                    type: 'text',
                    value: item.city ?? '',
                  }),
                },
              ],
            },
          },
        ]}
      />
    </ResponsivePage.Body>
  )
}

export default CollectionsPage
